import React from 'react';
import { useQuery } from '@apollo/client';
import { grey } from '@mui/material/colors';
import { Nullable, QueryResponse } from 'global';
import { ConditionBadge } from 'modules/equipment';
import { ImageViewer } from 'components/shared/Image';
import { Box, Button, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import { Launch, RemoveCircle, Speed, Tag, Grid3x3 } from '@mui/icons-material';

import { internalInventoryApi } from '../../api';
import { formatCurrency } from '../../../../utils';
import { NO_DATA } from '../../../../constants/common';
import { getAssetUrl } from '../../../../utils/getAssetUrl';
import { RenderWithFallback } from '../../../../components/shared/Conditional/RenderWithFallback';

interface Props {
  id: string;
  onRemove: () => void;
  onViewUnit: () => void;
}

export const InventoryCard = ({ id, onRemove, onViewUnit }: Props) => {
  const { data, loading, error } = useQuery<
    QueryResponse<{
      id: string;
      equipment: {
        id: string;
        assets: Array<{ id: string; source: string }>;
        price: {
          wholesaleCost: Nullable<number>;
          totalWholesaleCost: Nullable<number>;
        };
        details: {
          make: string;
          vin: string;
          year: string;
          model: string;
          name: string;
          class: string;
          mileage: string;
          stockId: string;
          condition: string;
          description: string;
        };
      };
    }>
  >(internalInventoryApi.getById, { variables: { input: { id } } });

  const inventory = data?.result?.entity ?? null;

  if (!inventory) return null;

  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      sx={{
        gap: '1rem',
        minHeight: '7rem',
        borderRadius: '0.5rem',
        padding: '0.5rem 1rem',
        alignItems: 'flex-start',
        border: `1px solid ${grey[300]}`,
      }}
    >
      <RenderWithFallback
        error={error}
        isLoading={loading}
        fallBack={<Skeleton sx={{ height: '10rem', width: '100%' }} variant="rectangular" />}
      >
        <>
          <IconButton className="icon-btn-bordered" size="small" color="error" onClick={onRemove}>
            <RemoveCircle fontSize="small" />
          </IconButton>

          <Stack gap="0.3rem" sx={{ flex: 5 }}>
            <Stack direction="row" justifyContent="space-between" flexWrap="wrap">
              <Stack direction="row" alignItems="center" gap="0.5rem" flexWrap="wrap">
                <Typography variant="h6">{inventory.equipment.details.name}</Typography>
                <ConditionBadge condition={inventory.equipment.details.condition} />
              </Stack>

              <Typography>
                List Price:{' '}
                {inventory.equipment.price.wholesaleCost !== null
                  ? formatCurrency(inventory.equipment.price.wholesaleCost, true)
                  : NO_DATA}
              </Typography>
            </Stack>

            <Stack direction="row" gap="1rem" flexWrap="wrap">
              <Stack direction="row" gap="0.3rem">
                <Tag fontSize="small" />
                <Typography>VIN: {inventory.equipment.details.vin || NO_DATA}</Typography>
              </Stack>
              <Stack direction="row" gap="0.3rem">
                <Speed fontSize="small" />
                <Typography>{inventory.equipment.details.mileage || NO_DATA}</Typography>
              </Stack>
              <Stack direction="row" gap="0.3rem">
                <Grid3x3 fontSize="small" />
                <Typography>Stock #:{inventory.equipment.details.stockId || NO_DATA}</Typography>
              </Stack>
            </Stack>

            {inventory.equipment.details.description && (
              <Stack>
                <Typography variant="caption">Vehicle Description:</Typography>
                <Typography variant="body1">{inventory.equipment.details.description}</Typography>
              </Stack>
            )}
          </Stack>

          <Box gap="0.3rem" sx={{ flex: 1, minWidth: '12rem' }}>
            <ImageViewer
              url={inventory.equipment.assets?.[0]?.source ? getAssetUrl(inventory.equipment.assets?.[0]?.source) : ''}
            />
          </Box>

          <Button size="small" endIcon={<Launch color="primary" />} onClick={onViewUnit}>
            View Unit
          </Button>
        </>
      </RenderWithFallback>
    </Stack>
  );
};
